import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Gift, TrendingUp, Award, Lock, Loader2, Scissors, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import LeaderboardPreview from '../components/LeaderboardPreview';

const Rewards = () => {
  const { currentUser } = useAuth();

  const [rewards, setRewards] = useState([]);
  const [userPoints, setUserPoints] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    const fetchRewardsData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const [catalogRes, pointsRes, historyRes] = await Promise.all([
          api.get('/api/rewards/catalog'),
          api.get('/api/rewards/points'),
          api.get('/api/rewards/history?limit=10'),
        ]);
        setRewards(catalogRes.data);
        setUserPoints(pointsRes.data);
        setPointsHistory(historyRes.data);
      } catch (error) {
        console.error('Error fetching rewards data:', error);
        toast.error('Failed to load rewards data');
      } finally {
        setLoading(false);
      }
    };

    fetchRewardsData();
  }, [currentUser]);

  const handleRedeem = async (reward) => {
    if (!userPoints || userPoints.points < reward.points_required) {
      toast.error(`Not enough points. Need ${reward.points_required - (userPoints?.points || 0)} more points.`);
      return;
    }

    setRedeeming(reward.id);

    try {
      const response = await api.post('/api/rewards/redeem', { reward_id: reward.id });
      toast.success(`Redeemed: ${response.data.reward_name}!`, {
        description: `New balance: ${response.data.new_balance} points`,
      });

      const [pointsRes, historyRes] = await Promise.all([
        api.get('/api/rewards/points'),
        api.get('/api/rewards/history?limit=10'),
      ]);
      setUserPoints(pointsRes.data);
      setPointsHistory(historyRes.data);
    } catch (error) {
      console.error('Error redeeming reward:', error);
      toast.error(error.response?.data?.detail || 'Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  if (!currentUser) {
    return (
      <div className="sauce-sity-page" data-testid="rewards-page-guest">
        <div className="sauce-sity-content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <Award size={80} style={{ margin: '0 auto', color: 'var(--ss-green)' }} />
          <h1 className="ss-title-xl ss-mt-24">Loyalty <span className="ss-highlight-purple">Rewards</span></h1>
          <p className="ss-text ss-mb-32">Sign in to view your rewards and points</p>
          <Link to="/login">
            <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="rewards-signin-btn">Sign In</button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sauce-sity-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={48} className="animate-spin" style={{ color: 'var(--ss-green)' }} />
      </div>
    );
  }

  const points = userPoints?.points || 0;
  const totalEarned = userPoints?.total_earned || 0;

  const howToEarn = [
    { icon: <Scissors size={28} style={{ color: 'var(--ss-green)' }} />, title: 'Book Services', desc: 'Earn 2 points per $5 spent on any service' },
    { icon: <Users size={28} style={{ color: 'var(--ss-purple)' }} />, title: 'Refer Friends', desc: 'Get bonus points when friends book or join' },
    { icon: <Crown size={28} style={{ color: 'var(--ss-green)' }} />, title: 'Join Membership', desc: 'Unlock exclusive rewards and benefits' },
  ];

  return (
    <div className="sauce-sity-page" data-testid="rewards-page">
      <div className="sauce-sity-content">
        <div className="ss-section-header ss-mb-48">
          <h1 className="ss-title-xl">
            <span className="ss-highlight-purple">Loyalty</span> Rewards
          </h1>
          <p className="ss-section-subtitle">Earn points with every visit and unlock exclusive rewards</p>
        </div>

        {/* Points Overview */}
        <div className="ss-grid-3 ss-mb-48">
          <div className="ss-stat-box ss-card-outline-green" data-testid="points-balance">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 8, color: 'var(--ss-white-soft)' }}>
              <Award size={16} /> <span>Your Points</span>
            </div>
            <div className="ss-stat-number" style={{ fontSize: '2.5rem' }}>{points}</div>
            <div className="ss-stat-label">Available to redeem</div>
          </div>

          <div className="ss-stat-box" style={{ borderColor: 'rgba(161,0,255,0.30)' }} data-testid="points-total-earned">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 8, color: 'var(--ss-white-soft)' }}>
              <TrendingUp size={16} /> <span>Total Earned</span>
            </div>
            <div className="ss-stat-number" style={{ fontSize: '2.5rem', color: 'var(--ss-purple)' }}>{totalEarned}</div>
            <div className="ss-stat-label">Lifetime points</div>
          </div>

          <div className="ss-stat-box" data-testid="points-visits">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 8, color: 'var(--ss-white-soft)' }}>
              <Gift size={16} /> <span>Total Visits</span>
            </div>
            <div className="ss-stat-number" style={{ fontSize: '2.5rem' }}>{currentUser.total_visits || 0}</div>
            <div className="ss-stat-label">Services completed</div>
          </div>
        </div>

        {/* Leaderboard preview */}
        <div className="ss-mb-48">
          <LeaderboardPreview compact />
        </div>

        {/* Membership Multiplier Banner */}
        {(() => {
          const tier = (currentUser?.membership_tier || 'none').toLowerCase();
          const isMember = tier === 'basic' || tier === 'vip';
          const multiplier = tier === 'vip' ? '2×' : tier === 'basic' ? '1.5×' : null;
          return (
            <div
              className="ss-card ss-mb-48"
              data-testid="membership-multiplier-banner"
              style={{
                background: isMember
                  ? 'linear-gradient(135deg, rgba(161,0,255,0.18) 0%, rgba(170,255,0,0.12) 100%)'
                  : 'linear-gradient(135deg, rgba(30,30,30,0.9) 0%, rgba(50,20,80,0.4) 100%)',
                border: isMember ? '1.5px solid rgba(170,255,0,0.4)' : '1px solid rgba(161,0,255,0.3)',
                padding: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                flexWrap: 'wrap',
              }}
            >
              <div style={{
                width: 64, height: 64, borderRadius: 999,
                background: isMember ? 'rgba(170,255,0,0.2)' : 'rgba(161,0,255,0.2)',
                border: isMember ? '1.5px solid rgba(170,255,0,0.5)' : '1.5px solid rgba(161,0,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Crown size={32} style={{ color: isMember ? 'var(--ss-green)' : 'var(--ss-purple)' }} />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                {isMember ? (
                  <>
                    <h3 className="ss-title-md" style={{ margin: 0, color: 'var(--ss-green)' }}>
                      You earn <span style={{ fontSize: '1.6rem', fontWeight: 900 }}>{multiplier}</span> points on every appointment
                    </h3>
                    <p className="ss-text" style={{ margin: '4px 0 0', opacity: 0.85 }}>
                      As a <strong>{tier.toUpperCase()}</strong> member, your loyalty points multiply automatically on every completed service.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="ss-title-md" style={{ margin: 0 }}>
                      Members earn <span style={{ color: 'var(--ss-green)', fontWeight: 900 }}>1.5×</span> or <span style={{ color: 'var(--ss-purple)', fontWeight: 900 }}>2×</span> points
                    </h3>
                    <p className="ss-text" style={{ margin: '4px 0 0', opacity: 0.85 }}>
                      Basic members earn 1.5× points, VIP members earn 2× points — automatically applied on every appointment.
                    </p>
                  </>
                )}
              </div>
              {!isMember && (
                <Link to="/membership" data-testid="upgrade-membership-cta" style={{ textDecoration: 'none' }}>
                  <button className="ss-btn ss-btn-primary" style={{ whiteSpace: 'nowrap' }}>
                    Upgrade →
                  </button>
                </Link>
              )}
            </div>
          );
        })()}

        {/* How to Earn */}
        <div className="ss-card ss-card-purple ss-mb-48" data-testid="how-to-earn">
          <h2 className="ss-title-lg ss-mb-24" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TrendingUp size={22} style={{ color: 'var(--ss-green)' }} /> How to Earn Points
          </h2>
          <div className="ss-grid-3">
            {howToEarn.map((item, i) => (
              <div key={i} className="ss-text-center">
                <div style={{
                  background: 'rgba(170,255,0,0.12)',
                  width: 64, height: 64,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 12,
                }}>{item.icon}</div>
                <h3 className="ss-title-md">{item.title}</h3>
                <p className="ss-text">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Available Rewards */}
        <div className="ss-mb-48" data-testid="available-rewards">
          <h2 className="ss-title-lg ss-mb-24">Available <span className="ss-highlight-green">Rewards</span></h2>
          <div className="ss-grid-3">
            {rewards.map((reward) => {
              const canRedeem = points >= reward.points_required;
              const progress = Math.min((points / reward.points_required) * 100, 100);

              return (
                <div
                  key={reward.id}
                  className={canRedeem ? 'ss-card ss-card-outline-green' : 'ss-card ss-card-dark'}
                  data-testid={`reward-${reward.id}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div style={{
                      background: 'rgba(20,20,20,0.9)',
                      width: 52, height: 52,
                      borderRadius: '999px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.5rem',
                      border: '1px solid var(--ss-border)',
                    }}>{reward.icon}</div>
                    {canRedeem ? (
                      <span style={{ background: 'var(--ss-green)', color: '#111', padding: '4px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800 }}>Ready!</span>
                    ) : (
                      <span style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--ss-white-soft)', padding: '4px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Lock size={12} /> Locked
                      </span>
                    )}
                  </div>
                  <h3 className="ss-title-md">{reward.name}</h3>
                  <p className="ss-text ss-mb-16" style={{ fontSize: '0.875rem' }}>{reward.description}</p>

                  <div className="ss-mb-16">
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: 6 }}>
                      <span style={{ color: 'var(--ss-white-soft)' }}>Progress</span>
                      <span style={{ color: canRedeem ? 'var(--ss-green)' : 'var(--ss-white-soft)', fontWeight: 700 }}>
                        {points}/{reward.points_required} pts
                      </span>
                    </div>
                    <div style={{ height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{
                        width: `${progress}%`, height: '100%',
                        background: canRedeem
                          ? 'linear-gradient(90deg, var(--ss-green), #d7ff5a)'
                          : 'linear-gradient(90deg, var(--ss-purple), #c56cff)',
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                  </div>

                  <button
                    onClick={() => handleRedeem(reward)}
                    disabled={!canRedeem || redeeming === reward.id}
                    data-testid={`redeem-${reward.id}`}
                    className={canRedeem ? 'ss-btn ss-btn-primary ss-btn-full' : 'ss-btn ss-btn-secondary ss-btn-full'}
                    style={{ opacity: (!canRedeem || redeeming === reward.id) ? 0.6 : 1, cursor: canRedeem ? 'pointer' : 'not-allowed' }}
                  >
                    {redeeming === reward.id ? (
                      <><Loader2 size={16} className="animate-spin" /> Redeeming…</>
                    ) : canRedeem ? (
                      'Redeem Now'
                    ) : (
                      `Need ${reward.points_required - points} more points`
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        {pointsHistory.length > 0 && (
          <div className="ss-card" data-testid="recent-activity">
            <h2 className="ss-title-lg ss-mb-24">Recent Activity</h2>
            <div>
              {pointsHistory.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                    borderBottom: '1px solid var(--ss-border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40, height: 40,
                      borderRadius: '999px',
                      background: t.points > 0 ? 'rgba(170,255,0,0.12)' : 'rgba(255,80,80,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {t.points > 0
                        ? <TrendingUp size={18} style={{ color: 'var(--ss-green)' }} />
                        : <Gift size={18} style={{ color: '#ff6b6b' }} />}
                    </div>
                    <div>
                      <p style={{ color: 'var(--ss-white)', fontWeight: 600, margin: 0 }}>{t.description}</p>
                      <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.8rem', margin: 0 }}>
                        {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div style={{
                    fontWeight: 800,
                    color: t.points > 0 ? 'var(--ss-green)' : '#ff6b6b',
                  }}>
                    {t.points > 0 ? '+' : ''}{t.points}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Rewards;
